import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import {
  DEFAULT_VOW_PROPERTY_CREATION_STEPS,
  PROPERTY_CREATION_STEP_STATUS,
  buildSequentialStepStatuses,
  buildResultStepStatuses,
} from '../constants/propertyCreationWorkflow';
import { clearStoredOutreachRef } from '../utils/outreachRef';

const DEFAULT_STEP_ESTIMATED_MS = 2200;

/** How long a step stays "in progress" in the simulated animation. */
export const getStepDuration = (step) => {
  const ms = Number(step?.estimatedMs);
  return Number.isFinite(ms) && ms > 0 ? ms : DEFAULT_STEP_ESTIMATED_MS;
};

/**
 * Shared controller for the property-creation progress modal.
 *
 * Drives the same animated progress + results flow used by:
 *  - "Add property from a selected MLS listing" (MyPropertyPage)
 *  - "Sign up with an outreach ref code" via email verification (Signup)
 *  - "Sign up with an outreach ref code" via Google OAuth (OAuthCallback)
 *
 * All flows finish on the results phase and navigate to
 * `/my-property/{propertyId}` when the user confirms.
 */
export default function usePropertyCreationProgress() {
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('Preparing your listing');
  const [subtitle, setSubtitle] = useState('');
  const [steps, setSteps] = useState(DEFAULT_VOW_PROPERTY_CREATION_STEPS);
  const [stepStatuses, setStepStatuses] = useState(
    buildSequentialStepStatuses(
      DEFAULT_VOW_PROPERTY_CREATION_STEPS,
      0,
      PROPERTY_CREATION_STEP_STATUS.IN_PROGRESS
    )
  );
  const [result, setResult] = useState(null);
  const [targetId, setTargetId] = useState(null);

  const stepTimeoutRef = useRef(null);
  const stepIndexRef = useRef(0);
  const closeTimeoutRef = useRef(null);

  const stopAnimation = useCallback(() => {
    if (stepTimeoutRef.current) {
      clearTimeout(stepTimeoutRef.current);
      stepTimeoutRef.current = null;
    }
  }, []);

  const clearCloseTimeout = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const close = useCallback(() => {
    stopAnimation();
    clearCloseTimeout();
    setOpen(false);
    setResult(null);
    setTargetId(null);
  }, [stopAnimation, clearCloseTimeout]);

  const scheduleClose = useCallback((delayMs = 900) => {
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      close();
    }, delayMs);
  }, [clearCloseTimeout, close]);

  const start = useCallback((options = {}) => {
    const {
      title: nextTitle = 'Preparing your listing',
      subtitle: nextSubtitle = 'Please wait while we set up your property details.',
      steps: nextSteps = DEFAULT_VOW_PROPERTY_CREATION_STEPS,
    } = options;

    const resolvedSteps = Array.isArray(nextSteps) && nextSteps.length
      ? nextSteps
      : DEFAULT_VOW_PROPERTY_CREATION_STEPS;

    clearCloseTimeout();
    stopAnimation();
    stepIndexRef.current = 0;

    setResult(null);
    setTargetId(null);
    setTitle(nextTitle);
    setSubtitle(nextSubtitle);
    setSteps(resolvedSteps);
    setStepStatuses(
      buildSequentialStepStatuses(
        resolvedSteps,
        0,
        PROPERTY_CREATION_STEP_STATUS.IN_PROGRESS
      )
    );
    setOpen(true);

    // The backend does all the work in one request, so the steps advance on
    // a clock paced by each step's `estimatedMs`. The last step stays active
    // until the real response arrives through showResult().
    const scheduleAdvance = (index) => {
      if (index >= resolvedSteps.length - 1) {
        return;
      }
      stepTimeoutRef.current = setTimeout(() => {
        const next = index + 1;
        stepIndexRef.current = next;
        setStepStatuses(
          buildSequentialStepStatuses(
            resolvedSteps,
            next,
            PROPERTY_CREATION_STEP_STATUS.IN_PROGRESS
          )
        );
        scheduleAdvance(next);
      }, getStepDuration(resolvedSteps[index]));
    };
    scheduleAdvance(0);
  }, [clearCloseTimeout, stopAnimation]);

  const finalize = useCallback((statuses = {}, nextSubtitle = '') => {
    stopAnimation();

    if (nextSubtitle) {
      setSubtitle(nextSubtitle);
    }

    setStepStatuses((prev) => ({
      ...prev,
      ...statuses,
    }));
  }, [stopAnimation]);

  /**
   * Switch the modal to its results phase. Keeps the modal open with a
   * confirm button that navigates to the new property dashboard.
   *
   * @param {object} response The onboarding/outreach response object.
   * @param {number|string} propertyId The created property id to navigate to.
   */
  const showResult = useCallback((response, propertyId) => {
    stopAnimation();
    clearCloseTimeout();

    setStepStatuses((prev) => ({
      ...prev,
      ...buildResultStepStatuses(response),
    }));
    setResult(response);
    setTargetId(propertyId ?? null);
    setOpen(true);
  }, [stopAnimation, clearCloseTimeout]);

  /**
   * Seed the signed-in user's property from an outreach reference while the
   * modal animates. The stored reference is consumed first so a refresh or a
   * second sign-in never seeds the same property twice.
   *
   * Resolves to `{ propertyCreated, propertyId, seedData }`. When a property
   * was created the modal stays open in its results phase; otherwise it closes.
   * Request errors close the modal and are rethrown for the caller to report.
   */
  const seedFromOutreach = useCallback(async (outreachRef, options = {}) => {
    const ref = typeof outreachRef === 'string' ? outreachRef.trim() : '';
    if (!ref) {
      return { propertyCreated: false, propertyId: null, seedData: null };
    }

    clearStoredOutreachRef();

    start({
      title: options.title || 'Creating your property from MLS history',
      subtitle: options.subtitle || 'We are creating your listing and preparing the core materials.',
      steps: options.steps,
    });

    try {
      const response = await api.post('/my-property/outreach/seed', { outreach_ref: ref });
      const seedData = response?.data || response;
      const propertyId = seedData?.property_id || seedData?.seeded_property?.id || null;
      const propertyCreated = Boolean(seedData?.property_created) && Boolean(propertyId);

      if (propertyCreated) {
        showResult(seedData, propertyId);
      } else {
        close();
      }

      return { propertyCreated, propertyId, seedData };
    } catch (err) {
      close();
      throw err;
    }
  }, [start, showResult, close]);

  const confirm = useCallback(() => {
    const id = targetId;
    close();
    if (id) {
      navigate(`/my-property/${id}`);
    }
  }, [targetId, close, navigate]);

  useEffect(() => {
    return () => {
      stopAnimation();
      clearCloseTimeout();
    };
  }, [stopAnimation, clearCloseTimeout]);

  return {
    start,
    finalize,
    showResult,
    seedFromOutreach,
    scheduleClose,
    close,
    confirm,
    targetId,
    modalProps: {
      open,
      title,
      subtitle,
      steps,
      stepStatuses,
      result,
      onConfirm: confirm,
    },
  };
}
